import * as yaml from 'js-yaml';

export interface YamlInput {
  rawValue: string;
  isValid: boolean;
  parsedObject: object | null;
  formattedString: string | null;
  error: string | null;
}

export class YamlParser {
  /**
   * Parse YAML string into a YamlInput object
   */
  static parse(yamlString: string): YamlInput {
    const trimmedInput = yamlString.trim();
    
    if (!trimmedInput) {
      return {
        rawValue: yamlString,
        isValid: false,
        parsedObject: null,
        formattedString: null,
        error: 'Empty YAML input'
      };
    }

    try {
      const parsedObject = yaml.load(trimmedInput);
      
      // Ensure we have an object (not null, undefined, or primitive)
      if (typeof parsedObject !== 'object' || parsedObject === null) {
        return {
          rawValue: yamlString,
          isValid: false,
          parsedObject: null,
          formattedString: null,
          error: 'YAML must parse to an object'
        };
      }

      // Format as pretty-printed JSON
      const formattedString = JSON.stringify(parsedObject, null, 2);

      return {
        rawValue: yamlString,
        isValid: true,
        parsedObject,
        formattedString,
        error: null
      };
    } catch (error) {
      return {
        rawValue: yamlString,
        isValid: false,
        parsedObject: null,
        formattedString: null,
        error: error instanceof Error ? error.message : 'Invalid YAML syntax'
      };
    }
  }

  /**
   * Validate YAML syntax without full parsing
   */
  static validateYaml(yamlString: string): boolean {
    if (!yamlString.trim()) {
      return false;
    }

    try {
      yaml.load(yamlString);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Convert YAML string to JSON string
   */
  static toJson(yamlString: string): string {
    const result = this.parse(yamlString);
    
    if (!result.isValid || !result.parsedObject) {
      throw new Error(result.error || 'Invalid YAML');
    }
    
    return result.formattedString || '';
  }

  /**
   * Convert YAML to JavaScript object
   */
  static toObject(yamlString: string): object | null {
    const result = this.parse(yamlString);
    return result.parsedObject;
  }

  /**
   * Check if YAML string is valid
   */
  static isValid(yamlString: string): boolean {
    return this.parse(yamlString).isValid;
  }

  /**
   * Get error message from invalid YAML
   */
  static getError(yamlString: string): string | null {
    const result = this.parse(yamlString);
    return result.error;
  }

  /**
   * Parse YAML with options
   */
  static parseWithOptions(yamlString: string, options: yaml.LoadOptions = {}): YamlInput {
    const trimmedInput = yamlString.trim();
    
    if (!trimmedInput) {
      return {
        rawValue: yamlString,
        isValid: false,
        parsedObject: null,
        formattedString: null,
        error: 'Empty YAML input'
      };
    }

    try {
      const parsedObject = yaml.load(trimmedInput, options);
      
      if (typeof parsedObject !== 'object' || parsedObject === null) {
        return {
          rawValue: yamlString,
          isValid: false,
          parsedObject: null,
          formattedString: null,
          error: 'YAML must parse to an object'
        };
      }

      const formattedString = JSON.stringify(parsedObject, null, 2);

      return {
        rawValue: yamlString,
        isValid: true,
        parsedObject,
        formattedString,
        error: null
      };
    } catch (error) {
      return {
        rawValue: yamlString,
        isValid: false,
        parsedObject: null,
        formattedString: null,
        error: error instanceof Error ? error.message : 'Invalid YAML syntax'
      };
    }
  }

  /**
   * Convert object to YAML string (reverse operation)
   */
  static fromObject(obj: object, options?: yaml.DumpOptions): string {
    try {
      return yaml.dump(obj, {
        indent: 2,
        lineWidth: -1,
        noRefs: true,
        sortKeys: false,
        ...options
      });
    } catch (error) {
      throw new Error(`Failed to convert object to YAML: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Compare two YAML strings for semantic equality
   */
  static compare(yaml1: string, yaml2: string): boolean {
    try {
      const obj1 = yaml.load(yaml1);
      const obj2 = yaml.load(yaml2);
      
      return JSON.stringify(obj1) === JSON.stringify(obj2);
    } catch {
      return false;
    }
  }

  /**
   * Get YAML schema information
   */
  static getSchema(yamlString: string): {
    types: string[];
    keys: string[];
    depth: number;
  } {
    try {
      const obj = yaml.load(yamlString);
      
      const extractTypes = (obj: any, depth = 0): string[] => {
        if (depth > 10) return ['max-depth-reached']; // Prevent infinite recursion

        const types = [typeof obj as string];
        
        if (obj && typeof obj === 'object') {
          if (Array.isArray(obj)) {
            obj.forEach((item, index) => {
              if (index < 5) { // Limit array analysis
                types.push(...extractTypes(item, depth + 1));
              }
            });
          } else {
            Object.entries(obj).forEach(([_key, value], index) => {
              if (index < 10) { // Limit object analysis
                types.push(...extractTypes(value, depth + 1));
              }
            });
          }
        }
        
        return types;
      };

      const extractKeys = (obj: any): string[] => {
        if (!obj || typeof obj !== 'object') return [];
        
        const keys: string[] = [];
        
        if (Array.isArray(obj)) {
          obj.forEach((item, index) => {
            keys.push(`[${index}]`);
            keys.push(...extractKeys(item));
          });
        } else {
          Object.keys(obj).forEach(key => {
            keys.push(key);
            keys.push(...extractKeys(obj[key]));
          });
        }
        
        return keys;
      };

      const getDepth = (obj: any): number => {
        if (!obj || typeof obj !== 'object') return 0;
        
        let maxDepth = 0;
        
        if (Array.isArray(obj)) {
          obj.forEach(item => {
            const depth = getDepth(item);
            if (depth > maxDepth) maxDepth = depth;
          });
        } else {
          Object.values(obj).forEach(value => {
            const depth = getDepth(value);
            if (depth > maxDepth) maxDepth = depth;
          });
        }
        
        return maxDepth + 1;
      };

      return {
        types: [...new Set(extractTypes(obj))],
        keys: extractKeys(obj),
        depth: getDepth(obj)
      };
    } catch {
      return {
        types: [],
        keys: [],
        depth: 0
      };
    }
  }
}